export function buildCanvasClickHandlers({
    mainstore,
    selectItems,
    uniquePrimitives,
    onPrimitiveClickPre,
    resolveRenderContext,
}) {
    const resolveRenderContextId = (node) => {
        let current = node;
        while (current) {
            if (current.attrs?.renderContextId) {
                return current.attrs.renderContextId;
            }
            current = current.getParent?.() ?? current.parent;
        }
        return undefined;
    };

    const resolveContextFromNode = (node) => {
        if (typeof resolveRenderContext !== "function") {
            return undefined;
        }
        const renderContextId = resolveRenderContextId(node);
        if (!renderContextId) {
            return undefined;
        }
        return resolveRenderContext(renderContextId);
    };

    const POOL_OWNER_TYPES = ["summary", "query", "categorizer", "search", "action", "flowcontrol"];
    const samePrimitiveSet = (a = [], b = []) => {
        if (a.length !== b.length) {
            return false;
        }
        const bIds = new Set(b.map((item) => item?.id).filter(Boolean));
        return a.every((item) => item?.id && bIds.has(item.id));
    };
    const deriveHierarchyPool = (seedList = []) => {
        const owners = uniquePrimitives(
            (seedList ?? []).flatMap((item) => {
                if (!item) {
                    return [];
                }
                if (POOL_OWNER_TYPES.includes(item.type)) {
                    return [item];
                }
                const owner = item.findParentPrimitives?.({ type: POOL_OWNER_TYPES, first: true })?.[0];
                return owner ? [owner] : [];
            })
        );
        const pool = uniquePrimitives(
            owners.flatMap((owner) => {
                const sourceContent = owner?.primitives?.source?.allItems ?? [];
                if (sourceContent.length > 0) {
                    return sourceContent;
                }
                return (
                    owner?.itemsForProcessingWithOptions?.(undefined, { forceImports: true }) ??
                    owner?.itemsForProcessing ??
                    []
                );
            })
        );
        return pool;
    };

    // ── viz_block handler ────────────────────────────────────────────────
    // VizDSL overlay Rects carry _blockId (the DSL block id) and
    // _vizSummaryId (the summary primitive that holds provenance data).
    // The summary's structured_summary_provenance maps "blockId.field" →
    // { evidence, verified, sourceIds }.  We filter by the clicked block
    // to get field-level source attribution per block.
    const resolveVizBlockSources = (kG) => {
        const blockId = kG?.attrs?._blockId;
        let summaryId = kG?.attrs?._vizSummaryId;
        if (!summaryId) {
            let current = kG;
            while (current) {
                const name = current.attrs?.name ?? current.name?.() ?? "";
                if (name === "viz_dsl" || current.attrs?._vizSummaryId) {
                    summaryId = current.attrs._vizSummaryId;
                    break;
                }
                current = current.getParent?.() ?? current.parent;
            }
        }
        if (!summaryId) return null;
        const summary = mainstore.primitive(summaryId);
        if (!summary) return null;

        const provenance = summary?.referenceParameters?.structured_summary_provenance ?? {};
        let sourceIds = [];
        let provenances = []; // matching provenance entries with evidence
        if (blockId && typeof provenance === "object") {
            const exact = provenance[blockId];
            if (exact?.sourceIds?.length) {
                sourceIds = [...exact.sourceIds];
                provenances = [{ key: blockId, ...exact }];
            } else {
                const prefix = blockId + ".";
                const idSet = new Set();
                const hasDot = blockId.includes(".");
                for (const [provKey, prov] of Object.entries(provenance)) {
                    if (provKey.startsWith(prefix) && prov?.sourceIds?.length) {
                        prov.sourceIds.forEach(id => idSet.add(id));
                        provenances.push({ key: provKey, ...prov });
                    }
                }
                // Container fallback (column headers, etc.) — only for non-field IDs
                if (provenances.length === 0 && !hasDot) {
                    for (const [provKey, prov] of Object.entries(provenance)) {
                        if (prov?.sourceIds?.length) {
                            prov.sourceIds.forEach(id => idSet.add(id));
                            provenances.push({ key: provKey, ...prov });
                        }
                    }
                }
                sourceIds = [...idSet];
            }
        }
        if (sourceIds.length === 0) {
            sourceIds = kG?.attrs?._vizSourceIds ?? [];
        }
        const sourceList = sourceIds.map(pid => mainstore.primitive(pid)).filter(Boolean);
        if (!summary && sourceList.length === 0 && sourceIds.length === 0) return null;
        return { summary, sourceList, sourceIds, provenances, blockId };
    };

    return {
        onClick: {
            viz_block: (id, pageId, data, kG) => {
                const resolved = resolveVizBlockSources(kG);
                if (!resolved) {
                    const context = resolveContextFromNode(kG);
                    if (context?.primitive) {
                        selectItems(context.primitive, { forFlow: true });
                    }
                    return;
                }
                const { summary, sourceList, sourceIds, provenances, blockId } = resolved;
                const uniqueSources = uniquePrimitives(sourceList);
                if (uniqueSources.length > 0) {
                    selectItems(summary, {
                        forFlow: true,
                        vizProvenance: { blockId, provenances, sourceList: uniqueSources, summaryId: summary.id },
                    });
                } else if (sourceIds && sourceIds.length > 0) {
                    selectItems(summary, {
                        forFlow: true,
                        vizProvenance: { blockId, provenances, sourceIds, summaryId: summary.id },
                    });
                } else if (summary) {
                    // No sources at all — show empty provenance panel so it updates
                    selectItems(summary, {
                        forFlow: true,
                        vizProvenance: { blockId, provenances: [], sourceIds: [], summaryId: summary.id },
                    });
                }
            },
            column_header: (id, pageId, data, kG) => {
                const colIdx = id?.[0]?.split("_")[1];
                if (colIdx === undefined) {
                    return;
                }
                const context = resolveContextFromNode(kG);
                if (!context?.dataTable || !context?.primitive) {
                    return;
                }
                const list = uniquePrimitives(
                    context.dataTable.cells
                        .filter((cell) => cell.cIdx === colIdx)
                        .flatMap((cell) => cell.items)
                );
                if (list.length > 0) {
                    selectItems(context.primitive, { forFlow: true, asList: true, list });
                }
            },
            row_header: (id, pageId, data, kG) => {
                const rowIdx = id?.[0]?.split("_")[1];
                if (rowIdx === undefined) {
                    return;
                }
                const context = resolveContextFromNode(kG);
                if (!context?.dataTable || !context?.primitive) {
                    return;
                }
                const list = uniquePrimitives(
                    context.dataTable.cells
                        .filter((cell) => cell.rIdx === rowIdx)
                        .flatMap((cell) => cell.items)
                );
                if (list.length > 0) {
                    selectItems(context.primitive, { forFlow: true, asList: true, list });
                }
            },
            frame: (id) => {
                const prim = mainstore.primitive(id);
                if (prim) {
                    selectItems(prim, { forFlow: true, asList: prim.type === "view" });
                }
            },
            primitive: (id, pageId, data, kG) => {
                if (typeof onPrimitiveClickPre === "function") {
                    const handled = onPrimitiveClickPre(id, pageId, data, kG);
                    if (handled) {
                        return;
                    }
                }
                const context = resolveContextFromNode(kG);
                if (context?.plainObject?.type === "image_gallery") {
                    selectItems(context.primitive ?? id, {
                        forFlow: true,
                        imageGallery: context.plainObject,
                    });
                    return;
                }
                if (context?.primitiveList?.length) {
                    const summarySearchLists = [
                        context?.directPrimitiveList,
                        context?.firstLevelPrimitiveList,
                        context?.primitiveList,
                    ];
                    const summaryItem = summarySearchLists
                        .find((list) => Array.isArray(list) && list.some((item) => item?.type === "summary"))
                        ?.find((item) => item?.type === "summary");
                    const summaryItems = summarySearchLists
                        .flatMap(list => Array.isArray(list) ? list.filter(item => item?.type === "summary") : []);
                    if (summaryItem) {
                        const directSources = uniquePrimitives([
                            ...(summaryItem.primitives?.relationship?.allItems ?? []),
                            ...(summaryItem.primitives?.source?.allItems ?? []),
                            ...(summaryItem.primitives?.link?.allItems ?? []),
                        ]);
                        const parentForPool =
                            summaryItem.findParentPrimitives?.({ type: ["summary", "query", "categorizer", "search", "action", "flowcontrol"], first: true })?.[0] ??
                            summaryItem;
                        const poolItems = uniquePrimitives(
                            parentForPool?.itemsForProcessingWithOptions?.(undefined, { forceImports: true }) ??
                            parentForPool?.itemsForProcessing ??
                            []
                        );
                        const list = directSources.length > 0 ? directSources : poolItems;
                        selectItems(context.primitive ?? id, {
                            forFlow: true,
                            asList: true,
                            list,
                            sourceList: directSources,
                            poolList: poolItems,
                            summaryPrimitive: summaryItem,
                            summaryPrimitives: summaryItems,
                        });
                        return;
                    }
                    const directList = uniquePrimitives(context?.firstLevelPrimitiveList ?? context?.directPrimitiveList ?? []);
                    const expandedList = uniquePrimitives(context.primitiveList ?? []);
                    const sourceList = directList;
                    const poolList = uniquePrimitives(
                        deriveHierarchyPool(sourceList.length > 0 ? sourceList : expandedList)
                    );
                    const resolvedPoolList = poolList.length > 0 ? poolList : expandedList;
                    const defaultList = sourceList.length > 0 ? sourceList : resolvedPoolList;
                    if (sourceList.length > 0 && resolvedPoolList.length > 0 && !samePrimitiveSet(sourceList, resolvedPoolList)) {
                        selectItems(context.primitive ?? id, {
                            forFlow: true,
                            asList: true,
                            list: defaultList,
                            sourceList,
                            poolList: resolvedPoolList
                        });
                    } else {
                        selectItems(context.primitive ?? id, { forFlow: true, asList: true, list: defaultList });
                    }
                    return;
                }
                if (context?.sourcePrimitiveIds?.length) {
                    const list = context.sourcePrimitiveIds
                        .map((pid) => mainstore.primitive(pid))
                        .filter(Boolean);
                    if (list.length > 0) {
                        selectItems(context.primitive ?? id, { forFlow: true, asList: true, list });
                        return;
                    }
                }
                selectItems(id, { forFlow: true });
            },
            cell: (id, pageId, data, kG) => {
                const cell = data?.cellId ?? id?.[0];
                const context = resolveContextFromNode(kG);
                if (!context?.dataTable || !context?.primitive) {
                    return;
                }
                const dataset = context.dataTable;
                const allocationField = data?.allocationField;
                if (allocationField !== undefined) {
                    const allocationIdx = data?.allocationIdx;
                    const baseCell = dataset.cells?.[0];
                    const allocationList = baseCell?.allocations?.[allocationField] ?? [];
                    const matchByIdx = allocationList.find((entry) => `${entry.idx}` === `${allocationIdx}`);
                    const match = matchByIdx;
                    const items = match?.items ?? [];
                    if (items.length > 0) {
                        selectItems(context.primitive, { forFlow: true, asList: true, list: items });
                        return;
                    }
                }
                const cellData = dataset.cells.find((c) => c.id === cell);
                if (cellData?.items?.length) {
                    selectItems(context.primitive, { forFlow: true, asList: true, list: cellData.items });
                    return;
                }
                const list = uniquePrimitives(dataset.cells.flatMap((entry) => entry.items));
                if (list.length > 0) {
                    selectItems(context.primitive, { forFlow: true, asList: true, list });
                }
            }
        }
    };
}
